import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';
import { NgbActiveModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs/operators';
import { PtCoreActions, PtCoreSelectors } from '@pt/pt-core';
import {
  DepartmentService,
  FolderService,
  LegalEntityService, SystemService,
  TreatmentCategoryService,
  TreatmentService,
  UsersService,
} from '@pt/api-client';
import { TreatmentActions, ValidatorGenerator } from '@pt/pt-shared';
import {
  IconType,
  IoniconsOutline,
  NavigationData,
  PtUiAppRoutes,
  REQUIRED_VALIDATOR_KEY,
} from '@pt/pt-models';
import { DuplicateTreatmentPayload } from '../../../models';
import { TreatmentDocCreateRequest } from '@primatoo-ms-organization';
import {
  Folder,
  LegalEntity,
  TreatmentCategory,
} from '@ms-organization-entities';
import { TreatmentType } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/TreatmentType';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { ListDepartmentForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/department/ListDepartmentForMenuResponse';
import { ListUserForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/user/ListUserForMenuResponse';
import { Department } from 'it/primatoo/openapi/api/org/schemas/department/Department';
import { User } from 'it/primatoo/openapi/api/org/schemas/user/listuserformenuresponse/User';

@Component({
  selector: 'pt-create-treatment-dialog',
  templateUrl: './create-treatment-dialog.component.html',
  styles: [
    `
      .modal-body--create {
        /*min-height: 466px;*/
        min-height: 175px;
      }

      .modal-body--duplicate {
        min-height: 175px;
      }

      .loader-container {
        /*height: 434px;*/
        height: 175px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .form-readonly--custom {
        background: white !important;
      }

      .tree-selector {
        margin-top: 10px;
      }

      .tree-selector-clean {
        position: absolute;
        right: 7px;
        font-size: 7px !important;
        top: 8px;
      }
    `,
  ],
})
export class CreateTreatmentDialogComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() openOnSuccess = true;
  @Input() treatmentType: TreatmentType = TreatmentType.HOLDER;

  @Input() dialogMode: 'CREATE' | 'DUPLICATE' = 'CREATE';
  @Input() duplicateTreatmentPayload: DuplicateTreatmentPayload;

  @Input() selectedFolderId: number;
  @Input() selectedLegalEntityId: number;
  @Input() selectedTreatmentCategoryId: number;
  @Input() selectedDepartmentId: number;

  @ViewChild('popContentLegalEntity') popContentLegalEntity: NgbPopover;
  @ViewChild('popContentTreatmentCategory')
  popContentTreatmentCategory: NgbPopover;
  @ViewChild('popContentDepartment') popContentDepartment: NgbPopover;
  @ViewChild('popContentProcessOwner') popContentProcessOwner: NgbPopover;

  IconInfoCircle = IoniconsOutline.infoCircle;

  public org$: Observable<Organization> = this.store.select(
    PtCoreSelectors.organization
  );
  public editInternalIdEnable = false;

  public TreatmentType = TreatmentType;
  public folders: NavigationData[] = [];
  public legalEntity: NavigationData[] = [];
  public treatmentCategories: NavigationData[] = [];
  public departmentsNavData: NavigationData[] = [];
  public departmentProcessOwner: NavigationData[] = [];
  public departments: Department[] = [];

  public selectedFolder: NavigationData;
  public selectedLegalEntity: NavigationData;
  public selectedTreatmentCategory: NavigationData;
  public selectedDepartment: NavigationData;
  public selectedDepartmentProcessOwner: NavigationData;

  public loading = false;
  public createFailed = false;
  public formGroup: FormGroup;
  public REQUIRED = ValidatorGenerator.generateWidgetValidator(
    REQUIRED_VALIDATOR_KEY
  );

  private fields: string[] = [];

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private users: Array<User> = [];
  public errorMessage?: string;

  IconOpen = IoniconsOutline.mail;
  email?: string;

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    public actionsSubject: ActionsSubject,
    private folderService: FolderService,
    private departmentService: DepartmentService,
    private treatmentService: TreatmentService,
    private legalEntityService: LegalEntityService,
    private treatmentCategoryService: TreatmentCategoryService,
    private userService: UsersService,
    private systemService: SystemService
  ) {
    this.systemService.getSupportInfo().subscribe(
      (success) => {
        this.email = success.email;
      },
      () => {
        this.email = undefined;
      }
    );
  }

  ngAfterViewInit(): void {
    this.initNavigationData();
  }

  setLoading(value: boolean, withDelay = false) {
    if (!withDelay) {
      setTimeout(() => {
        this.loading = value;
      });
    } else {
      setTimeout(() => {
        this.loading = value;
      }, 300);
    }
  }

  ngOnInit(): void {
    this.org$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
      this.editInternalIdEnable = org.editInternalIdEnable;
      this.alignDisabled();
    });

    this.actionsSubject
      .pipe(
        ofType(TreatmentActions.createTreatmentSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (action: {
          id: number;
          treatmentType:
            | 'HOLDER'
            | 'RESPONSIBLE'
            | 'PROJECT'
            | 'SUB_RESPONSIBLE';
        }) => {
          if (this.openOnSuccess) {
            this.openTreatment(action?.id);
          }
          this.activeModal.close(true);
        }
      );

    this.actionsSubject
      .pipe(
        ofType(TreatmentActions.createTreatmentFailed),
        takeUntil(this.destroy$)
      )
      .subscribe((action: { error: string }) => {
        this.loading = false;
        this.errorMessage = action.error;
        this.createFailed = true;
      });

    this.actionsSubject
      .pipe(
        ofType(TreatmentActions.duplicateTreatmentSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        if (this.openOnSuccess) {
          this.openTreatment(action?.response?.id);
        }
        this.activeModal.close(true);
      });
  }

  private openTreatment(id: number) {
    switch (this.treatmentType) {
      case TreatmentType.HOLDER:
        this.store.dispatch(
          PtCoreActions.navigateTo({
            route: [
              PtUiAppRoutes.BUSINESS_MANAGEMENT,
              PtUiAppRoutes.TREATMENTS_HOLDER,
              PtUiAppRoutes.EDIT,
              id.toString(),
            ],
          })
        );
        break;
      case TreatmentType.PROJECT:
        this.store.dispatch(
          PtCoreActions.navigateTo({
            route: [
              PtUiAppRoutes.BUSINESS_MANAGEMENT,
              PtUiAppRoutes.PROJECT_CARDS,
              PtUiAppRoutes.EDIT,
              id.toString(),
            ],
          })
        );
        break;
      case TreatmentType.RESPONSIBLE:
        this.store.dispatch(
          PtCoreActions.navigateTo({
            route: [
              PtUiAppRoutes.BUSINESS_MANAGEMENT,
              PtUiAppRoutes.TREATMENTS_RESPONSIBLE,
              PtUiAppRoutes.EDIT,
              id.toString(),
            ],
          })
        );
        break;
      case TreatmentType.SUB_RESPONSIBLE:
        this.store.dispatch(
          PtCoreActions.navigateTo({
            route: [
              PtUiAppRoutes.BUSINESS_MANAGEMENT,
              PtUiAppRoutes.TREATMENTS_SUB_RESPONSIBLE,
              PtUiAppRoutes.EDIT,
              id.toString(),
            ],
          })
        );
        break;
    }
  }

  private alignDisabled() {
    if (this.formGroup) {
      if (this.editInternalIdEnable) {
        this.formGroup.controls.internalId.enable();
      } else {
        this.formGroup.controls.internalId.disable();
      }
    }
  }

  private initNavigationData() {
    this.setLoading(true);
    forkJoin({
      department: this.departmentService.findAllForMenu(),
      folders: this.folderService.findAllFolders(),
      legalEntity: this.legalEntityService.getInternalLegalEntities(),
      treatmentCategories:
        this.treatmentCategoryService.getTreatmentCategories(),
      users: this.userService.getMenu(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: {
          department: ListDepartmentForMenuResponse;
          folders: Folder[];
          legalEntity: LegalEntity[];
          treatmentCategories: TreatmentCategory[];
          users: ListUserForMenuResponse;
        }) => {
          if (response.folders) {
            this.folders = response.folders.map((f) =>
              NavigationData.folderToNavigationData(f)
            );
          }
          if (response.legalEntity) {
            this.legalEntity = response.legalEntity
              .filter((l) => !l.deleted)
              .map((l) => NavigationData.legalEntityToNavigationData(l));
          }
          if (response.treatmentCategories) {
            this.treatmentCategories = response.treatmentCategories
              .filter((t) => !t.deleted)
              .map((t) => NavigationData.treatmentCategoryToNavigationData(t));
          }
          if (response.department) {
            this.departments = response.department.departments;
            this.departmentsNavData = this.departments.map((t) =>
              NavigationData.departmentToNavigationData(t)
            );
          }
          if (response.users) {
            this.users = response.users.users;
            this.departmentProcessOwner = this.users.map((t) =>
              NavigationData.userToNavigationData(t)
            );
          }
          this.initFormGroup();
        }
      );
  }

  private initFormGroup() {
    switch (this.dialogMode) {
      case 'CREATE':
        if (
          this.selectedFolderId !== null &&
          this.selectedFolderId !== undefined &&
          this.folders
        ) {
          this.selectedFolder = this.folders?.find(
            (f) => f.id == this.selectedFolderId
          );
        }

        if (
          this.selectedLegalEntityId !== null &&
          this.selectedLegalEntityId !== undefined &&
          this.legalEntity
        ) {
          this.selectedLegalEntity = this.legalEntity?.find(
            (f) => f.id == this.selectedLegalEntityId
          );
        }

        if (
          this.selectedTreatmentCategoryId !== null &&
          this.selectedTreatmentCategoryId !== undefined &&
          this.treatmentCategories
        ) {
          this.selectedTreatmentCategory = this.treatmentCategories?.find(
            (f) => f.id == this.selectedLegalEntityId
          );
        }

        if (
          this.selectedDepartmentId !== null &&
          this.selectedDepartmentId !== undefined &&
          this.departmentsNavData
        ) {
          this.selectedDepartment = this.departmentsNavData?.find(
            (f) => f.id == this.selectedLegalEntityId
          );
        }

        this.formGroup = new FormGroup({
          name: new FormControl(null, [
            Validators.required,
            Validators.maxLength(128),
          ]),
          description: new FormControl(null),
          internalId: new FormControl(null),
          treatmentCategoryId: new FormControl(
            this.selectedTreatmentCategory?.name
          ),
          departmentId: new FormControl(this.selectedDepartment?.name),
          processOwnerId: new FormControl(
            this.selectedDepartmentProcessOwner?.name
          ),
          legalEntityId: new FormControl(this.selectedLegalEntity?.name, [
            Validators.required,
          ]),
        });
        this.alignDisabled();
        break;
      case 'DUPLICATE':
        this.selectedFolder = this.folders?.find(
          (f) => f.id == this.duplicateTreatmentPayload?.payload?.parentId
        );
        this.selectedLegalEntity = this.legalEntity?.find(
          (f) => f.id == this.duplicateTreatmentPayload?.payload?.legalEntityId
        );
        this.selectedTreatmentCategory = this.treatmentCategories?.find(
          (f) =>
            f.id == this.duplicateTreatmentPayload?.payload?.treatmentCategoryId
        );
        this.selectedDepartment = this.departmentsNavData?.find(
          (f) => f.id == this.duplicateTreatmentPayload?.payload?.departmentId
        );
        this.selectedDepartmentProcessOwner = this.departmentProcessOwner?.find(
          (f) => f.id == this.duplicateTreatmentPayload?.payload?.processOwnerId
        );

        this.treatmentService
          .getToOverride(this.treatmentType)
          .pipe(takeUntil(this.destroy$))
          .subscribe((response) => {
            this.fields = Object.keys(response).filter(
              (r) => response[r] === true
            );

            this.formGroup = new FormGroup({
              name: new FormControl(null, [
                Validators.required,
                Validators.maxLength(128),
              ]),
              internalId: new FormControl(null),
              description: new FormControl(
                this.duplicateTreatmentPayload?.payload?.description
              ),
              treatmentCategoryId: new FormControl(
                this.selectedTreatmentCategory?.name
              ),
              departmentId: new FormControl(this.selectedDepartment?.name),
              processOwnerId: new FormControl(
                this.selectedDepartmentProcessOwner?.name
              ),
              legalEntityId: new FormControl(this.selectedLegalEntity?.name, [
                Validators.required,
              ]),
            });
            this.alignDisabled();
          });
        break;
    }
    this.setLoading(false, true);
  }

  checkUsedField(field: string): boolean {
    switch (this.dialogMode) {
      case 'CREATE':
        return true;
      case 'DUPLICATE':
        return this.fields?.includes(field);
      default:
        return true;
    }
  }

  confirm() {
    this.setLoading(true);

    let request = null;
    switch (this.dialogMode) {
      case 'CREATE':
        request = {
          name: this.formGroup.controls.name.value,
          description: this.formGroup.controls.description.value,
          internalId: this.formGroup.controls.internalId.value,
          legalEntityId: this.selectedLegalEntity?.id,
          parentId: this.selectedFolder ? this.selectedFolder?.id : null,
          treatmentCategoryId: this.selectedTreatmentCategory?.id,
          departmentId: this.selectedDepartment?.id,
          treatmentType: this.treatmentType,
          processOwnerId: this.selectedDepartmentProcessOwner?.id,
        } as TreatmentDocCreateRequest;

        this.store.dispatch(
          TreatmentActions.createTreatment({
            request: request,
          })
        );
        break;
      case 'DUPLICATE':
        request = {
          name: this.formGroup.controls.name.value,
          description: this.formGroup.controls.description.value,
          internalId: this.formGroup.controls.internalId.value,
          legalEntityId: this.selectedLegalEntity?.id,
          parentId: this.selectedFolder ? this.selectedFolder?.id : null,
          treatmentCategoryId: this.selectedTreatmentCategory?.id,
          departmentId: this.selectedDepartment?.id,
          processOwnerId: this.selectedDepartmentProcessOwner?.id,
          treatmentType: this.treatmentType,
        } as TreatmentDocCreateRequest;

        this.store.dispatch(
          TreatmentActions.duplicateTreatment({
            id: this.duplicateTreatmentPayload.payload?.docId,
            request: request,
          })
        );
        break;
    }
  }

  selectLegalEntity(resource: NavigationData) {
    this.popContentLegalEntity.close();
    this.selectedLegalEntity = resource;
    this.formGroup.controls.legalEntityId.setValue(
      this.selectedLegalEntity?.name
    );
  }

  selectTreatmentCategory(resource: NavigationData) {
    this.popContentTreatmentCategory.close();
    this.selectedTreatmentCategory = resource;
    this.formGroup.controls.treatmentCategoryId.setValue(
      this.selectedTreatmentCategory?.name
    );
  }

  selectDepartment(resource: NavigationData) {
    this.popContentDepartment.close();
    this.selectedDepartment = resource;
    this.formGroup.controls.departmentId.setValue(
      this.selectedDepartment?.name
    );
    const dep = this.departments.find(
      (d) => d.id === this.selectedDepartment?.id
    );
    if (dep && dep.processOwnerId) {
      this.selectedDepartmentProcessOwner = this.departmentProcessOwner.find(
        (d) => d.id === dep.processOwnerId
      );
      this.formGroup.controls.processOwnerId.setValue(
        this.selectedDepartmentProcessOwner?.name
      );
    }
  }

  selectProcessOwner(resource: NavigationData) {
    this.popContentDepartment.close();
    this.selectedDepartmentProcessOwner = resource;
    this.formGroup.controls.processOwnerId.setValue(
      this.selectedDepartmentProcessOwner?.name
    );
  }

  chooseLegalEntity() {
    this.popContentLegalEntity.open();
  }

  chooseTreatmentCategory() {
    this.popContentTreatmentCategory.open();
  }

  chooseProcessOwner() {
    this.popContentProcessOwner.open();
  }

  chooseDepartment() {
    this.popContentDepartment.open();
  }

  cleanLegalEntity() {
    this.formGroup?.controls?.legalEntityId?.setValue(null);
    this.selectedLegalEntity = null;
  }

  cleanTreatmentCategory() {
    this.formGroup?.controls?.treatmentCategoryId?.setValue(null);
    this.selectedTreatmentCategory = null;
  }

  cleanDepartment() {
    this.formGroup?.controls?.departmentId?.setValue(null);
    this.selectedDepartment = null;
  }

  cleanProcessOwner() {
    this.formGroup?.controls?.processOwnerId?.setValue(null);
    this.selectedDepartmentProcessOwner = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  protected readonly IconType = IconType;
}
